import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, TextField, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { FS } from "@plannel/utils/font-util";
import { C } from "@plannel/components/ai/aiTokens";
import { addedSx } from "@plannel/pages/ai/term/TermAliasCell";
import { toneDark, toneMain, toneTint } from "@plannel/pages/ai/term/termColors";
import { ALIAS_MAX_LEN, LOCALE_LABELS, isValidAlias } from "@plannel/pages/ai/term/termUtil";

// 한 언어의 별칭 줄 — 표준(X 로 제외, 되돌리기 아이콘으로 복원) + 추가(X 로 삭제) + 입력칸. 동작은 아이콘 클릭만(칩 본문은 누르지 않음)
const lower = (s) => String(s || "").toLowerCase();

// 그리드의 별칭 태그와 같은 모서리(4px) — 알약형이 아니라 둥근 사각
const CHIP_SX = { height: 22, fontSize: FS.BODY, borderRadius: "4px", "& .MuiChip-label": { px: 1 } };
// 표준 별칭은 그리드 태그와 같은 옅은 회색 채움(테두리 없음)
const STD_CHIP_SX = { ...CHIP_SX, bgcolor: C.bg3, color: C.text1 };
// 칩 동작 아이콘 — 채운 원 + 흰 글리프(추가 별칭의 기본 삭제 아이콘과 같은 모양). 색은 칩 종류에 맞춤
// 칩 자체 스타일이 아이콘 크기·색을 덮으므로 칩 sx 에서 지정한다
const iconSx = (bg, hover) => ({
  "& .MuiChip-deleteIcon": {
    fontSize: 10, p: "2px", boxSizing: "content-box", borderRadius: "50%", mr: "5px", ml: "-2px",
    bgcolor: bg, color: "common.white", "&:hover": { bgcolor: hover, color: "common.white" },
  },
});
const ICON_TONE = {
  // 표준 칩의 X 는 칩 바탕(옅은 회색)과 같은 계열로 — 도드라지지 않게
  std: iconSx("grey.400", "grey.600"),
  excluded: iconSx(toneMain("excluded"), toneDark("excluded")),
  override: iconSx(toneMain("override"), toneDark("override")),
  added: iconSx(toneMain("added"), toneDark("added")),
};
const EXCLUDED_LABEL = { textDecoration: "line-through" };
const EXCLUDED_CHIP_SX = { color: toneMain("excluded"), borderColor: toneMain("excluded") };

// 툴팁은 아이콘에만 — Tooltip 이 Chip 이 넘기는 className·onClick 을 아이콘에 그대로 전달한다
const tipIcon = (Icon, title) => <Tooltip title={title} placement="top"><Icon /></Tooltip>;

const TermAliasRow = ({ locale, standard = [], added = [], excluded = [], tone = "override", autoFocus = false, onChange }) => {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [invalid, setInvalid] = useState(false);
  const gone = new Set(excluded.map(lower));
  const stdSet = new Set(standard.map(lower));
  // 표준에서 이미 빠진 제외어도 보여 준다 — 저장 시 서버가 거절하므로 사용자가 지울 수 있어야 한다
  const stale = excluded.filter((a) => !stdSet.has(lower(a)));
  const changed = added.length > 0 || excluded.length > 0;

  const toggleStandard = (a) => {
    const next = gone.has(lower(a)) ? excluded.filter((x) => lower(x) !== lower(a)) : [...excluded, a];
    onChange({ added, excluded: next });
  };

  const commit = () => {
    const v = input.trim();
    if (!v) return;
    if (!isValidAlias(v)) {
      setInvalid(true);
      return;
    }
    const exists = [...standard, ...added].some((a) => lower(a) === lower(v));
    if (!exists) onChange({ added: [...added, v], excluded });
    setInput("");
    setInvalid(false);
  };

  return (
    <Box className="term-alias-row" sx={{
      display: "flex", alignItems: "flex-start", gap: 1, py: 0.75, px: 1, borderRadius: 1,
      bgcolor: changed ? toneTint(tone, 0.04) : "transparent",
    }}>
      <Typography sx={{ width: 88, flexShrink: 0, pt: 0.25, fontSize: FS.BODY, color: "text.secondary" }}>
        {LOCALE_LABELS[locale]}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
        {standard.map((a) => (gone.has(lower(a)) ? (
          // 제외 중 — 주황 취소선 + 늘 보이는 되돌리기 아이콘(제외 상태의 단서)
          <Chip key={`s-${a}`} size="small" variant="outlined"
            onDelete={() => toggleStandard(a)} deleteIcon={tipIcon(UndoIcon, t("aiTermUndoExclude"))}
            sx={{ ...CHIP_SX, ...EXCLUDED_CHIP_SX, ...ICON_TONE.excluded }} label={<Box component="span" sx={EXCLUDED_LABEL}>{a}</Box>} />
        ) : (
          <Chip key={`s-${a}`} size="small"
            onDelete={() => toggleStandard(a)} deleteIcon={tipIcon(CloseIcon, t("aiTermExcludeAlias"))}
            sx={{ ...STD_CHIP_SX, ...ICON_TONE.std }} label={a} />
        )))}
        {stale.map((a) => (
          <Tooltip key={`x-${a}`} title={t("aiTermStandardRemoved")}>
            <Chip size="small" variant="outlined" label={a}
              sx={{ ...CHIP_SX, ...EXCLUDED_CHIP_SX, ...EXCLUDED_LABEL, borderStyle: "dashed" }} onDelete={() => toggleStandard(a)} />
          </Tooltip>
        ))}
        {added.map((a) => (
          <Chip key={`a-${a}`} size="small" label={a}
            sx={{ ...CHIP_SX, ...addedSx(tone), ...ICON_TONE[tone] }}
            deleteIcon={tipIcon(CloseIcon, t("delete"))}
            onDelete={() => onChange({ added: added.filter((x) => x !== a), excluded })} />
        ))}
        <TextField size="small" variant="standard" value={input} placeholder={t("aiTermAliasAdd")} autoFocus={autoFocus}
          error={invalid} helperText={invalid ? t("aiTermAliasInvalid") : ""}
          inputProps={{ maxLength: ALIAS_MAX_LEN + 1 }}
          onChange={(e) => { setInput(e.target.value); setInvalid(false); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.preventDefault();
              commit();
            }
          }}
          onBlur={commit}
          // 입력칸은 그 언어 줄 어디에든 마우스가 올라오면(또는 포커스) 드러난다 — 입력칸 위에서만 반응하면 찾기 어렵다
          sx={{
            minWidth: 160, flex: 1, "& input": { fontSize: FS.BODY, py: 0.25 },
            "& .MuiInput-underline:before": { borderBottomColor: "transparent" },
            ".term-alias-row:hover & .MuiInput-underline:not(.Mui-error):before": { borderBottomColor: "rgba(0, 0, 0, 0.42)" },
            "& input::placeholder": { opacity: 0, transition: "opacity 120ms" },
            ".term-alias-row:hover & input::placeholder, & .Mui-focused input::placeholder": { opacity: 0.6 },
          }} />
      </Box>
    </Box>
  );
};

export default TermAliasRow;
